import { Div, Text } from "atomize"
import { theme } from "../settings/config"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faCircleExclamation,
	faSyncAlt,
	faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons"
import { DateTime } from "luxon"
import { useContext } from "react"
import { schoolContext } from "../providers/school.provider"
import { profileContext } from "../providers/profile.provider"
import { sharedDataContext } from "../providers/sharedData.provider"
import {
	getLessonColor,
	getPtrLevel,
	getSatLevel,
	getSportIcon,
	lessonTypeHasCourt,
} from "../utils/func.utils"
import { getSchoolRates, isAssistantIn, lacksPaymentMethod } from "../utils/billing.utils"
import { isCancelledLesson } from "../utils/cancellation.utils"
import LessonTypeLabel from "../atoms/LessonTypeLabel"
import { CARD_TEXT } from "./lessonCardText"
import { formatMinutes, lessonStartMinutes } from "../utils/week.utils"

// "Si sovrappone alla lezione delle 18:00", "…alle lezioni delle 18:00 e
// delle 19:30": quello che il triangolino dice a chi ci si ferma sopra.
const overlapTitle = lessons => {
	const times = lessons.map(lesson => `delle ${formatMinutes(lessonStartMinutes(lesson))}`)
	return lessons.length === 1
		? `Si sovrappone alla lezione ${times[0]}`
		: `Si sovrappone alle lezioni ${times.slice(0, -1).join(", ")} e ${times[times.length - 1]}`
}

// `overlapsWith` sono le lezioni del giorno con cui questa si accavalla
// (`getLessonOverlaps`): chi le guarda lo vede accanto all'orario.
function LessonHeader({ lessonData, overlapsWith }) {
	const { getSelectedSchool, selectedSchool } = useContext(schoolContext)
	const { coach } = useContext(profileContext)
	const { getLessonType, getSport } = useContext(sharedDataContext)

	// qualcuno non ha scritto come paga: si vede anche a lezione chiusa, come
	// nel riepilogo del profilo. All'assistente no: incassa il titolare.
	const missingPayment =
		!isAssistantIn(lessonData, coach?.coachID) &&
		lacksPaymentMethod(lessonData, getSchoolRates(coach?.billingData, selectedSchool))

	// il livello del SAT, se è stato detto: un pallino del colore della palla
	const satLevel = getSatLevel(lessonData.satLevel)
	// e quello PTR del CAD: il numero e basta, "CAD · 3.0"
	const ptrLevel = getPtrLevel(lessonData.ptrLevel)

	return (
		<Div w="100%" d="flex" align="center" justify="space-between">
			<Div d="flex" align="center" style={{ gap: "0.5rem" }}>
				<Text
					// sul telefono vola nella riga della settimana (vedi MorphingLesson)
					data-morph="time"
					textColor={theme.colors.gunmetal}
					style={{
						fontSize: CARD_TEXT.line,
						lineHeight: 1.4,
						...(isCancelledLesson(lessonData) && { textDecoration: "line-through" }),
					}}>
					{DateTime.fromJSDate(lessonData.lessonStart.toDate()).toFormat("HH:mm")}{" "}
					-{" "}
					{DateTime.fromJSDate(lessonData.lessonEnd.toDate()).toFormat("HH:mm")}
				</Text>
				{lessonTypeHasCourt(getLessonType(lessonData.lessonType)) &&
					getSelectedSchool()?.coaches[lessonData.coachID]?.taughtSports.length > 1 && (
						<>
							·
							<Div
								w="fit-content"
								d="flex"
								align="center"
								style={{ gap: "0.25rem" }}
								rounded="15px">
								<Text textColor={theme.colors.gunmetal}>
									{getSport(lessonData.sportID)?.sportName}
								</Text>
								<FontAwesomeIcon
									icon={getSportIcon(lessonData.sportID)}
									color={theme.colors.gunmetal}
								/>
							</Div>
						</>
					)}
				{lessonData.recurrencyID && (
					<>
						·
						<FontAwesomeIcon icon={faSyncAlt} color={theme.colors.gunmetal} />
					</>
				)}
				{overlapsWith?.length > 0 && (
					<>
						·
						<span
							role="img"
							title={overlapTitle(overlapsWith)}
							aria-label={overlapTitle(overlapsWith)}>
							<FontAwesomeIcon icon={faTriangleExclamation} color={theme.colors.warning} />
						</span>
					</>
				)}
				{missingPayment && (
					<>
						·
						<span title="Manca il metodo di pagamento">
							<FontAwesomeIcon icon={faCircleExclamation} color={theme.colors.red} />
						</span>
					</>
				)}
			</Div>
			<Text textColor={theme.colors.gunmetal} style={{ fontSize: CARD_TEXT.line, lineHeight: 1.4 }}>
				{/* sullo sfondo della lezione, che è quello del suo tipo */}
				<LessonTypeLabel
					lessonTypeID={lessonData.lessonType}
					lessonType={getLessonType(lessonData.lessonType)}
					background={getLessonColor(getLessonType(lessonData.lessonType))}
				/>
				{satLevel && (
					<>
						{" · "}
						<span
							title={`Livello ${satLevel.label}`}
							style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
							<span
								style={{
									display: "inline-block",
									width: "0.6rem",
									height: "0.6rem",
									borderRadius: "50%",
									background: theme.colors[satLevel.color],
								}}
							/>
							{satLevel.label}
						</span>
					</>
				)}
				{ptrLevel && (
					<span title={`Livello PTR ${ptrLevel.label}`}>
						{" · "}
						{ptrLevel.label}
					</span>
				)}
				{isCancelledLesson(lessonData) && (
					<Text tag="span" textWeight="bold">
						{" · Annullata"}
					</Text>
				)}
			</Text>
		</Div>
	)
}

export default LessonHeader
